import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook for managing loading state with progress
 * @param {Object} options - Configuration options
 * @param {boolean} options.simulateProgress - Whether to simulate progress for operations without progress tracking
 * @param {number} options.simulationDuration - Duration of the simulation in ms (default: 3000)
 * @returns {Object} Loading state and handlers
 */
export const useLoading = (options = {}) => {
  const { simulateProgress = false, simulationDuration = 3000 } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("Loading...");

  const progressIntervalRef = useRef(null);

  // Clear any running interval when unmounting
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  /**
   * Start loading with optional progress simulation
   * @param {string} message - Initial loading message
   */
  const startLoading = useCallback(
    (message = "Loading...") => {
      setIsLoading(true);
      setProgress(0);
      setProgressMessage(message);

      if (simulateProgress) {
        // Clear any existing interval
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }

        // Set up progress simulation
        const startTime = Date.now();
        progressIntervalRef.current = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const percentage = Math.min((elapsed / simulationDuration) * 90, 90);
          setProgress(percentage); // Max at 90% so we can jump to 100% when done

          if (percentage >= 90) {
            clearInterval(progressIntervalRef.current);
          }
        }, 100);
      }
    },
    [simulateProgress, simulationDuration]
  );

  /**
   * Update progress value
   * @param {number} value - Progress value (0-100)
   * @param {string} message - Optional message to update
   */
  const updateProgress = useCallback((value, message = null) => {
    setProgress(value);
    if (message) {
      setProgressMessage(message);
    }
  }, []);

  /**
   * Complete loading and clean up
   */
  const finishLoading = useCallback(() => {
    // Clear any simulation interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    // Jump to 100% to indicate completion
    setProgress(100);

    // Optional: wait briefly before hiding to show the 100%
    setTimeout(() => {
      setIsLoading(false);
      setProgress(0);
    }, 500);
  }, []);

  /**
   * Wrapper function to run an async task with loading state
   * @param {Function} asyncTask - The async function to execute
   * @param {Object} options - Loading options
   */
  const executeWithLoading = useCallback(
    async (asyncTask, options = {}) => {
      const {
        startMessage = "Loading...",
        successMessage = "Completed",
        onProgress = null, // Optional callback for handling progress updates
      } = options;

      try {
        startLoading(startMessage);

        // Execute the task, potentially with progress updates
        const result = await asyncTask(
          onProgress
            ? (progress, message) => updateProgress(progress, message)
            : null
        );

        updateProgress(100, successMessage);
        return result;
      } catch (error) {
        throw error;
      } finally {
        finishLoading();
      }
    },
    [startLoading, updateProgress, finishLoading]
  );

  return {
    isLoading,
    progress,
    progressMessage,
    startLoading,
    updateProgress,
    finishLoading,
    executeWithLoading,
  };
};
